import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Button, Spinner, Alert } from 'reactstrap';
import { motion, AnimatePresence } from 'framer-motion';
import FeatherIcon from 'feather-icons-react';
import { uploadServiceContractFiles, validateFile } from '../helpers/fileUpload';

const modalVariants = {
  hidden: { opacity: 0, scale: 0.8, y: -50 },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: { type: 'spring', damping: 25, stiffness: 300 }
  },
  exit: { opacity: 0, scale: 0.8, y: 50 }
};

const ServiceReportUploadModal = ({ isOpen, toggle, onSkip, onUploadComplete }) => {
  const { t } = useTranslation();
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const fileInputRef = useRef(null);

  const addFilesWithValidation = (newFiles) => {
    const errors = [];
    const validFiles = [];

    newFiles.forEach(file => {
      const result = validateFile(file);
      if (result.valid) {
        validFiles.push(file);
      } else {
        errors.push(result.error);
      }
    });

    if (errors.length > 0) {
      setValidationErrors(prev => [...prev, ...errors]);
      // Auto-clear errors after 5 seconds
      setTimeout(() => setValidationErrors([]), 5000);
    }

    if (validFiles.length > 0) {
      setFiles(prev => [...prev, ...validFiles]);
    }
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    addFilesWithValidation(selectedFiles);
    // Reset input so same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const droppedFiles = Array.from(e.dataTransfer.files);
      addFilesWithValidation(droppedFiles);
    }
  };

  const dismissError = (index) => {
    setValidationErrors(prev => prev.filter((_, i) => i !== index));
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    if (files.length === 0) {
      onSkip();
      return;
    }

    setUploading(true);
    try {
      const urls = await uploadServiceContractFiles(files);
      if (urls && urls.length > 0) {
        onUploadComplete(urls);
      } else {
        console.error('Upload failed - no URLs returned');
        onSkip();
      }
    } catch (error) {
      console.error('Upload failed:', error);
      onSkip();
    } finally {
      setUploading(false);
      setFiles([]);
    }
  };

  const handleSkip = () => {
    setFiles([]);
    onSkip();
  };

  const handleClose = () => {
    if (!uploading) {
      setFiles([]);
      setValidationErrors([]);
      toggle();
    }
  };

  const getFileIcon = (file) => {
    if (file.type.startsWith('image/')) return 'image';
    if (file.type === 'application/pdf') return 'file-text';
    if (file.type.includes('word') || file.type.includes('document')) return 'file-text';
    if (file.type.includes('sheet') || file.type.includes('excel')) return 'file-text';
    return 'file';
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={handleClose}
      centered
      backdrop="static"
      size="md"
    >
      <AnimatePresence>
        {isOpen && (
          <motion.div
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            {/* Header */}
            <div className="modal-header border-0 pb-0">
              <div className="d-flex align-items-center">
                <div
                  className="bg-primary bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center me-3"
                  style={{ width: '48px', height: '48px' }}
                >
                  <FeatherIcon icon="upload-cloud" size={24} className="text-primary" />
                </div>
                <div>
                  <h5 className="mb-1 fw-bold">{t('shared.upload_service_report', 'Upload Service Report')}</h5>
                  <p className="text-muted small mb-0">{t('business.components.service_report_upload_modal.add_doc_subtitle', 'Add documentation before resolving')}</p>
                </div>
              </div>
              <button
                type="button"
                className="btn-close"
                onClick={handleClose}
                disabled={uploading}
              />
            </div>

            {/* Body */}
            <div className="modal-body pt-3">
              <p className="text-muted mb-4">
                {t('business.components.service_report_upload_modal.upload_confirmation', 'Would you like to upload a picture or service report before marking this ticket as resolved?')}
                <br />
                <small className="text-secondary">{t('business.components.service_report_upload_modal.skip_optional_step', 'This step is optional - you can skip if no documentation is needed.')}</small>
              </p>

              {/* Validation Errors */}
              {validationErrors.length > 0 && (
                <div className="mb-3">
                  {validationErrors.map((error, index) => (
                    <Alert
                      key={index}
                      color="danger"
                      className="py-2 mb-2 d-flex align-items-center justify-content-between"
                    >
                      <div className="d-flex align-items-center">
                        <FeatherIcon icon="alert-circle" size={16} className="me-2" />
                        <small>{error}</small>
                      </div>
                      <button
                        type="button"
                        className="btn-close btn-close-sm"
                        style={{ fontSize: '0.7rem' }}
                        onClick={() => dismissError(index)}
                      />
                    </Alert>
                  ))}
                </div>
              )}

              {/* Drop Zone */}
              <div
                className={`upload-dropzone border-2 border-dashed rounded-3 p-4 text-center mb-3 ${
                  dragActive ? 'border-primary bg-primary bg-opacity-10' : 'border-secondary'
                }`}
                style={{
                  transition: 'all 0.2s ease',
                  cursor: 'pointer'
                }}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
              >
                <FeatherIcon
                  icon="upload-cloud"
                  size={40}
                  className={dragActive ? 'text-primary mb-2' : 'text-muted mb-2'}
                />
                <p className="mb-1 fw-medium">
                  {dragActive ? t('shared.drop_files_here', 'Drop files here') : t('shared.drag_drop_files_here', 'Drag & drop files here')}
                </p>
                <p className="text-muted small mb-2">{t('shared.or_click_to_browse', 'or click to browse')}</p>
                <small className="text-secondary">
                  {t('shared.supports_images_pdf_word_excel', 'Supports: Images, PDF, Word, Excel')}
                </small>
                <input
                  type="file"
                  ref={fileInputRef}
                  multiple
                  hidden
                  onChange={handleFileChange}
                  accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
                />
              </div>

              {/* Selected Files */}
              {files.length > 0 && (
                <div className="selected-files">
                  <p className="fw-medium mb-2 small">
                    <FeatherIcon icon="paperclip" size={14} className="me-1" />
                    {t('shared.selected_files_count', 'Selected Files ({{count}})', { count: files.length })}
                  </p>
                  <div style={{ maxHeight: '150px', overflowY: 'auto' }}>
                    {files.map((file, idx) => (
                      <motion.div
                        key={idx}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        className="d-flex align-items-center bg-light rounded p-2 mb-2"
                      >
                        <div
                          className="bg-white rounded d-flex align-items-center justify-content-center me-2"
                          style={{ width: '32px', height: '32px' }}
                        >
                          <FeatherIcon icon={getFileIcon(file)} size={16} className="text-primary" />
                        </div>
                        <div className="flex-grow-1 overflow-hidden">
                          <p className="mb-0 small fw-medium text-truncate">{file.name}</p>
                          <small className="text-muted">{formatFileSize(file.size)}</small>
                        </div>
                        <button
                          type="button"
                          className="btn btn-sm btn-link text-danger p-1"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeFile(idx);
                          }}
                          disabled={uploading}
                        >
                          <FeatherIcon icon="x" size={16} />
                        </button>
                      </motion.div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="modal-footer border-0 pt-0">
              <Button
                color="light"
                onClick={handleSkip}
                disabled={uploading}
                className="px-4"
              >
                {t('shared.skip', 'Skip')}
              </Button>
              <Button
                color="primary"
                onClick={handleUpload}
                disabled={uploading}
                className="px-4"
              >
                {uploading ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    {t('shared.uploading', 'Uploading...')}
                  </>
                ) : files.length > 0 ? (
                  <>
                    <FeatherIcon icon="upload" size={16} className="me-2" />
                    {t('shared.upload_and_resolve', 'Upload & Resolve')}
                  </>
                ) : (
                  t('shared.resolve', 'Resolve')
                )}
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </Modal>
  );
};

export default ServiceReportUploadModal;
